/**
 * Authentication API service
 * Handles register, login, logout, and user profile
 */

import { api } from './api';

// Types matching worker domain
export type UserRole = 'user' | 'admin';
export type UserPlan = 'free' | 'professional' | 'team';
export type Profession =
  | 'avocat'
  | 'jurist'
  | 'consultant_fiscal'
  | 'notar'
  | 'executor_judecatoresc'
  | 'auditor'
  | 'contabil'
  | 'elev_student'
  | 'altele';

export interface User {
  id: number;
  email: string;
  displayName: string | null;
  role: UserRole;
  profession: Profession | null;
  plan: UserPlan;
  createdAt: string;
  updatedAt: string;
}

export interface Quota {
  searchesUsed: number;
  searchesLimit: number;
  consolidatedViewsUsed: number;
  consolidatedViewsLimit: number;
  resetAt: string;
}

export interface MeResponse {
  user: User;
  quota: Quota;
}

export interface RegisterInput {
  email: string;
  password: string;
  displayName?: string;
  profession?: Profession;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  message: string;
}

/**
 * Register a new user account
 */
export async function register(input: RegisterInput): Promise<AuthResponse> {
  return api.post<AuthResponse>('/auth/register', input);
}

/**
 * Login with email and password
 */
export async function login(input: LoginInput): Promise<AuthResponse> {
  return api.post<AuthResponse>('/auth/login', input);
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  return api.post<void>('/auth/logout');
}

/**
 * Get current authenticated user and quota info
 */
export async function getMe(): Promise<MeResponse> {
  return api.get<MeResponse>('/auth/me');
}

/**
 * Get quota information for the current user
 */
export async function getQuota(): Promise<Quota> {
  const { quota } = await getMe();
  return quota;
}

export interface UpdateProfileInput {
  display_name?: string;
  profession?: Profession | null;
}

export interface UpdateProfileResponse {
  id: number;
  email: string;
  display_name: string | null;
  role: UserRole;
  profession: Profession | null;
  plan: UserPlan;
  created_at: string;
  updated_at: string;
}

/**
 * Update user profile (display name and/or profession)
 */
export async function updateProfile(input: UpdateProfileInput): Promise<UpdateProfileResponse> {
  return api.patch<UpdateProfileResponse>('/user/profile', input);
}

export interface PortalResponse {
  url: string;
}

/**
 * Get Stripe Customer Portal URL for subscription management
 */
export async function getPortalUrl(): Promise<PortalResponse> {
  return api.get<PortalResponse>('/subscriptions/portal');
}
